import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { fetchRecentlyViewed } from '../../../actions/recentlyViewed.actions';
import { dashboardSection } from '../../../constants';
import {
	selectRecentlyViewedContents,
	selectRecentlyViewedStatus,
} from '../../../reducers/recentlyViewed.selectors';
import useAction from '../../../utils/useAction';
import NewCarousel from '../../Carousel/NewCarousel';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';
import { getCommonCardProps } from '../../Search/SearchResults/SearchResults';

// The row shows the 10 most recent items. The backend stores a longer history
// (for the future search "seen" badge), but this row only ever needs one
// page — request exactly what it renders.
export const MAX_ITEMS = 10;

const RecentlyViewedCarousel: React.FC = () => {
	const { t } = useTranslation();
	const fetchRecentlyViewedAction = useAction(fetchRecentlyViewed);
	const recentlyViewedItems = useSelector(selectRecentlyViewedContents);
	const status = useSelector(selectRecentlyViewedStatus);

	// Fetch from here rather than AppPage: this component only mounts inside the
	// new-dashboard treatment branch, so control users (the majority) never pay
	// for the history read. Refetch on every dashboard entry so items viewed
	// since the last visit show up.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		fetchRecentlyViewedAction({ limit: MAX_ITEMS, sortOrder: 'desc', version: 2 });
	}, []);

	// The row only exists once there is something to resume. No skeletons:
	// an empty-history user would otherwise get a skeleton row that flashes
	// and vanishes (layout jump), and a failed fetch must not break the
	// dashboard either. Deliberately NOT gated on in-flight status: START keeps
	// the previous contents, so a refetch on dashboard re-entry keeps the cached
	// cards on screen instead of unmounting and remounting the row.
	if (recentlyViewedItems.length === 0 || status === AsyncActionStatus.ERROR) return null;

	return (
		<NewCarousel title={t('Recently viewed')}>
			{recentlyViewedItems.slice(0, MAX_ITEMS).map((item) => (
				<DashboardMuiCard
					key={item.id}
					contentType={item.type === 'product' ? 'product' : (item.fileType ?? 'document')}
					{...getCommonCardProps(item)}
					dashboardSection={dashboardSection.recentlyViewed}
				/>
			))}
		</NewCarousel>
	);
};

export default React.memo(RecentlyViewedCarousel);
